// A device card for the room grid that shows its operable controls INLINE
// (no need to open a side panel for simple actions).
//
// Touch switches (1 / 2 / 4 gang) render one button per output plus All On /
// All Off. Interaction model (mirrors the mobile app):
//   - a 3-dots menu on the card       → device-level common settings
//   - RIGHT-CLICK an individual button → that output's schedule / details
// Every other device type falls back to the icon tile that opens the full
// control panel. The switch/case is the single place to extend inline controls
// to more device types (fan speed, curtain, …).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TuneIcon from '@mui/icons-material/Tune';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { deviceTypeKey, relayCount, fanCount } from '../../../core/devices/deviceMeta';
import { getOutputs } from '../../../core/api/api';
import { useRelayStatus } from '../../../core/hooks/useRelayStatus';
import { loadButtonSettings, marksFor, rowForOutput } from '../../../core/devices/buttonStatus';
import RelayTile from '../../shared/controls/RelayTile';
import { AllButtons } from '../../shared/controls/AllBar';
import FanDialControl from '../../shared/controls/FanDialControl';
import FanChannelRow from '../../shared/FanChannelRow';
import GlassPlateCard from './GlassPlateCard';
import { isGlassPlate } from '../../../core/devices/glassPlates';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';
import DoorControlCard from './DoorControlCard';
import LightControlCard from './LightControlCard';
import CurtainControlCard from './CurtainControlCard';
import GenericControlCard from './GenericControlCard';
import { BleLampNoticeBody } from '../pages/device/BleLampNotice';
import BleNodeCard from '../../shared/ble/BleNodeCard';
import { SceneControllerBody } from '../../shared/SceneControllerPanel';
import PhaseControlCard from './PhaseControlCard';
import SensorControlCard from './SensorControlCard';
import HubInfo from '../../shared/HubInfo';
import IrRemoteControlCard from './IrRemoteControlCard';
import { isSensorDevice } from '../../../core/devices/sensorState';
import { panelGridSx, CARD_TYPE } from '../../../core/devices/buttonLayout';
import ButtonSettingsDialog from '../../shared/ButtonSettingsDialog';

// ── The switch card, in the PHONE'S look (2026-10-03) ────────────────────────
//
// Same parts as the mobile TouchScreen — ui/shared/controls RelayTile (raised
// gradient, glow, green/red/blue power icon, schedule/timer/two-way badges) and
// AllButtons (the card-sized All Off / All On) — and the same hook, core/hooks/useRelayStatus, so the desktop, the
// phone browser and the APK cannot draw a device differently or disagree about
// its state. Before this the card had its own flat outlined pills.
//
// ⚠️ THE UI NEVER GUESSES — inherited from useRelayStatus. A press publishes and
// changes nothing; the colour moves when the DEVICE echoes. An optimistic flip
// lied about offline devices and alternated the command (1-1, 1-0, 1-1 on three
// presses of a dead switch). Unknown state is drawn BLUE, as on the phone.
const DESK_TILE_H = 62;

// Exported for the standalone desktop device page (pages/device/TouchScreen),
// which shows this same card bigger — one switch control on every surface.
export function TouchControlCard({ device, gangs, subtitle, fanChannels = 0 }) {
  const { userId } = useApp();
  const navigate = useNavigate();
  const uno = device.UniqueNumber;

  // Asked for switches + fan channels: the fan values ride PAST the switches in
  // the same frame (4+1 / 7+1 / 5+2), exactly as on the phone.
  const {
    states, known, values, toggleRelay, setAll, setChannel,
  } = useRelayStatus(uno, gangs + fanChannels, userId);
  // Only for the online dot: when a message last ARRIVED (see useDeviceStatus).
  const [, , liveAt] = useDeviceStatus(uno);

  const [outputs, setOutputs] = useState([]);
  const [settings, setSettings] = useState(null);   // { schedulesByOutput, mappings }
  const [ctx, setCtx] = useState(null);             // per-button right-click menu
  const [settingsFor, setSettingsFor] = useState(null);   // 1-based output

  // ⚠️ THE NAMES ARE FETCHED ONCE, ON MOUNT — so a rename re-reads them
  // (reloadOutputs), or it saves and the tiles keep the old name.
  const reloadOutputs = useCallback(() => {
    getOutputs(uno)
      .then((out) => setOutputs(Array.isArray(out?.data) ? out.data : []))
      .catch(() => { /* keep the names we have rather than blanking the card */ });
  }, [uno]);
  useEffect(() => { reloadOutputs(); }, [reloadOutputs]);

  // The badges: schedules per output and the two-way mappings (buttonStatus).
  const reloadSettings = useCallback(() => {
    loadButtonSettings(uno, gangs).then(setSettings).catch(() => {});
  }, [uno, gangs]);
  useEffect(() => { reloadSettings(); }, [reloadSettings]);

  // "Pending today" changes as time passes; re-evaluate without refetching.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const marks = useMemo(() => {
    if (!settings) return [];
    const weekday = now.getDay();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return Array.from({ length: gangs }, (_, i) => marksFor({
      output: i + 1,
      schedulesByOutput: settings.schedulesByOutput,
      mappings: settings.mappings,
      outputRow: rowForOutput(outputs, i + 1),
      uniqueNumber: uno,
      weekday,
      nowMinutes,
    }));
  }, [settings, outputs, uno, gangs, now]);

  // Matched on the Output NUMBER, never on array position (rows can come back
  // out of order or with a gap).
  const outName = (i) => rowForOutput(outputs, i + 1)?.OutputName || `Switch ${i + 1}`;

  const openButtonMenu = (e, i) => {
    e.preventDefault(); // suppress the browser's native context menu
    setCtx({ mouseX: e.clientX + 2, mouseY: e.clientY - 6, output: i + 1, name: outName(i) });
  };

  const goSchedule = (output, name) => {
    navigate(`/schedule/${uno}/${output}`, { state: { deviceId: device.DeviceID, name } });
  };

  const afterSave = () => { reloadOutputs(); reloadSettings(); };

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} live={liveAt} />
        {/* ⚠️ THE ⋮ MENU RENAMES BUTTONS TOO — so it re-reads the names. */}
        <DeviceCardMenu device={device} onChanged={afterSave} />
      </Box>

      {/* Laid out as the panel is: two rows, filling DOWN each column, so a
          6-gang reads 1 3 5 / 2 4 6 exactly as on the wall (buttonLayout). */}
      <Box sx={panelGridSx(gangs)}>
        {Array.from({ length: gangs }, (_, i) => (
          <Box key={i} onContextMenu={(e) => openButtonMenu(e, i)} sx={{ minWidth: 0 }}>
            <RelayTile
              name={outName(i)}
              isOn={!!states[i]}
              isKnown={!!known[i]}
              marks={marks[i]}
              onToggle={() => toggleRelay(i)}
              // Long-press = the phone's gesture; right-click is the desktop one.
              onSchedule={() => setSettingsFor(i + 1)}
              width="100%"
              height={DESK_TILE_H}
              iconSize={18}
              nameFont={11}
              compact
            />
          </Box>
        ))}
      </Box>

      {/* The fan channels of a 4+1 / 7+1 / 5+2 — the phone's row, under the
          switches. Nothing for a plain switch panel. */}
      {fanChannels > 0 && (
        <FanChannelRow values={values} firstIndex={gangs} count={fanChannels} onSet={setChannel} />
      )}

      {/* All Off / All On as one row under the switches (AllButtons) — the
          phone's arcs were clipped to slivers at card size. */}
      {gangs > 1 && <AllButtons onAll={setAll} />}

      {/* Per-button right-click — that output's schedule / settings. */}
      <Menu
        open={ctx !== null}
        onClose={() => setCtx(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctx ? { top: ctx.mouseY, left: ctx.mouseX } : undefined}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }}>
            {ctx?.name}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const c = ctx; setCtx(null); if (c) goSchedule(c.output, c.name); }}>
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Schedule</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const c = ctx; setCtx(null); if (c) setSettingsFor(c.output); }}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Button Settings</ListItemText>
        </MenuItem>
      </Menu>

      <ButtonSettingsDialog
        open={settingsFor != null}
        uniqueNumber={uno}
        output={settingsFor ?? 1}
        onClose={() => setSettingsFor(null)}
        onSaved={afterSave}
        onOpenSchedule={() => {
          const output = settingsFor;
          setSettingsFor(null);
          goSchedule(output, outName(output - 1));
        }}
      />
    </Card>
  );
}

function SceneControllerControlCard({ device, subtitle, bench }) {
  const uno = device.UniqueNumber;
  return (
    <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1.25, pb: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu device={device} />
      </Box>
      <SceneControllerBody device={device} compact bench={bench} />
    </Card>
  );
}

/** The Wi-Fi + BLE hub: live or not, and the ⋮ (firmware, Wi-Fi, info) — no buttons. */
function HubControlCard({ device, subtitle }) {
  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || device.UniqueNumber}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu device={device} />
      </Box>
      <HubInfo device={device} compact />
    </Card>
  );
}

function FanControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu device={device} />
      </Box>

      {/* The phone's dial (ui/shared/controls/FanDialControl): drag or tap
          round it; one command when the pointer lifts. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FanDialControl device={device} size={168} />
        <Typography color="text.secondary" sx={{ fontSize: CARD_TYPE.subtitle, mt: 0.25 }}>
          Drag around the dial to set speed
        </Typography>
      </Box>
    </Card>
  );
}

// Every device type renders an inline control card — the grid never opens a
// side window.
/** `bench`: the MQTT consoles — see SceneControllerBody. */
export default function DeviceControlCard({ device, subtitle, bench = false }) {
  // ⚠️ BEFORE the switch. A sensor has nothing to switch, so it must never
  // reach a card that draws buttons — or the generic one, which fetches its
  // absent outputs and reports "No controllable outputs". Types with a screen
  // of their own (the two current meters included) are excluded inside
  // isSensorDevice, so this cannot swallow one.
  if (isSensorDevice(device)) return <SensorControlCard device={device} subtitle={subtitle} />;

  // A glass product is drawn as its real plate (core/devices/glassPlates).
  if (isGlassPlate(device.DeviceID)) return <GlassPlateCard device={device} subtitle={subtitle} />;

  const key = deviceTypeKey(device.DeviceID);
  switch (key) {
    case 'oneTouch': return <TouchControlCard device={device} gangs={1} subtitle={subtitle} />;
    case 'twoTouch': return <TouchControlCard device={device} gangs={2} subtitle={subtitle} />;
    case 'fourTouch': return <TouchControlCard device={device} gangs={4} subtitle={subtitle} />;
    // Same gap as the device screen: neither of these had a case, so a 4+1 or a
    // 7+1 in a room grid drew generic toggles — including one for the FAN,
    // whose speed is not a boolean. The card now shows the switches AND the fan
    // channels (the phone's FanChannelRow), as the device screen does.
    case 'touch':
    case 'switchFan':
      return (
        <TouchControlCard
          device={device}
          gangs={relayCount(device.DeviceID)}
          fanChannels={key === 'switchFan' ? fanCount(device.DeviceID) : 0}
          subtitle={subtitle}
        />
      );
    case 'fan': return <FanControlCard device={device} subtitle={subtitle} />;
    case 'door': return <DoorControlCard device={device} subtitle={subtitle} />;
    case 'irBlaster': return <IrRemoteControlCard device={device} subtitle={subtitle} />;
    case 'curtain': return <CurtainControlCard device={device} subtitle={subtitle} />;
    case 'rgb':
    case 'wwh':
    case 'wwrgb': return <LightControlCard device={device} subtitle={subtitle} />;
    // All on / All off through the hub; the phone notice when there is none.
    case 'bleWwh':
      return (
        <BleNodeCard
          device={device}
          subtitle={subtitle}
          fallback={(dev, menu) => (
            <Card sx={{ bgcolor: 'primary.main', borderRadius: 3, position: 'relative' }}>
              <BleLampNoticeBody device={dev} compact />
              <Box sx={{ position: 'absolute', top: 4, right: 4, color: 'primary.contrastText' }}>{menu}</Box>
            </Card>
          )}
        />
      );
    case 'hub': return <HubControlCard device={device} subtitle={subtitle} />;
    case 'singlePhase':
    case 'threePhase': return <PhaseControlCard device={device} />;
    // Four scene buttons; the room's scenes are the controls. No outputs, so
    // the generic card would only report their absence.
    case 'sceneController': return <SceneControllerControlCard device={device} subtitle={subtitle} bench={bench} />;
    default: return <GenericControlCard device={device} subtitle={subtitle} />;
  }
}
